import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Container,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Chip,
    Fade,
    Slide,
    LinearProgress,
    Skeleton,
    Tabs,
    Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentsIcon from '@mui/icons-material/Payments';
import ColorData from 'Components/CstmTheme/ColorData';
import AdminApi from 'Service/Admin.api';
import { useDispatch } from 'react-redux';
import { startLoading, stopLoading } from '../../Redux/Screen/Action';
import { formatDate } from 'Utils/Common';

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`,
    minHeight: '100vh',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
    background: ColorData.primary.main,
    color: '#fff',
    borderRadius: theme.spacing(3),
    marginBottom: theme.spacing(4),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    background: ColorData.primary.main,
    color: '#fff',
    padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
        backgroundColor: `${theme.palette.action.hover}50`,
    },
    '&:hover': {
        backgroundColor: '#f1f1f1',
    },
}));

interface PayoutEntry {
    pkPayout: number;
    amount: number;
    professional: string; // Will show e-Mitra name based on backend changes
    submittedBy: string;
    caseTitle: string;
    date: string;
    caseId: number;
    status: number;
}

const EMitraPayouts = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [pendingPayouts, setPendingPayouts] = useState<PayoutEntry[]>([]);
    const [completedPayouts, setCompletedPayouts] = useState<PayoutEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    // Total calculation
    const totalPayouts = completedPayouts.reduce((sum, item) => sum + item.amount, 0);
    const totalPending = pendingPayouts.reduce((sum, item) => sum + item.amount, 0);

    useEffect(() => {
        fetchPayouts();
    }, []);

    const fetchPayouts = async () => {
        setLoading(true);
        dispatch(startLoading({ loading: true, loadtext: "Fetching Payouts..." }));
        try {
            const response = await AdminApi.getMyPayouts();
            let allPayouts: PayoutEntry[] = [];
            if (response && Array.isArray(response.data)) {
                allPayouts = response.data;
            } else if (response && Array.isArray(response)) {
                allPayouts = response;
            }

            // Backend Status map: PayoutPending = 0, PayoutCompleted = 1
            // PayoutEntry interface now includes `status: number`
            const completed = allPayouts.filter((p) => p.status === 1);
            const pending = allPayouts.filter((p) => p.status === 0);

            setCompletedPayouts(completed);
            setPendingPayouts(pending);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            dispatch(stopLoading());
        }
    };

    return (
        <StyledContainer maxWidth="xl">
            <Fade in timeout={800}>
                <Box sx={{ pb: 10 }}>
                    <HeaderCard elevation={4}>
                        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                            <Box display="flex" alignItems="center" gap={3}>
                                <PaymentsIcon sx={{ fontSize: 60 }} />
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" color="#fff">
                                        My Payouts
                                    </Typography>
                                    {/* <Typography variant="subtitle1" sx={{ opacity: 0.9, color: "#fff" }}>
                                        View your completed and approved payouts
                                    </Typography> */}
                                    <Box mt={2}>
                                        <Typography variant="h5" fontWeight="bold" color="#fff">
                                            Total Earned: ₹{totalPayouts.toLocaleString('en-IN')}
                                        </Typography>
                                        {/* <Typography variant="subtitle2" sx={{ opacity: 0.8, mt: 0.5, color: "#fff" }}>
                                            Pending: ₹{totalPending.toLocaleString('en-IN')}
                                        </Typography> */}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </HeaderCard>

                    <Tabs
                        value={tabValue}
                        onChange={(_, newValue) => setTabValue(newValue)}
                        sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 'bold' } }}
                    >
                        <Tab label={`Completed (${completedPayouts.length})`} />
                        <Tab label={`Pending (${pendingPayouts.length})`} />
                    </Tabs>

                    {loading ? (
                        <LinearProgress sx={{ mt: 2, '& .MuiLinearProgress-bar': { backgroundColor: ColorData.primary.main } }} />
                    ) : (
                        <Slide direction="up" in timeout={600}>
                            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <TableContainer sx={{ maxHeight: 600 }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell>Payout ID</StyledTableCell>
                                                <StyledTableCell>Case Title</StyledTableCell>
                                                <StyledTableCell>Case ID</StyledTableCell>
                                                <StyledTableCell>Amount</StyledTableCell>
                                                <StyledTableCell>Date</StyledTableCell>
                                                <StyledTableCell>Status</StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tabValue === 0 ? (
                                                completedPayouts.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                                            <Typography variant="h6" color="text.primary">No completed payouts found.</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    completedPayouts.map((p) => (
                                                        <StyledTableRow key={p.pkPayout}>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>#{p.pkPayout}</TableCell>
                                                            <TableCell>{p.caseTitle}</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>#{p.caseId}</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold', color: ColorData.text.primary }}>₹{p.amount.toLocaleString('en-IN')}</TableCell>
                                                            <TableCell>{formatDate(p.date)}</TableCell>
                                                            <TableCell>
                                                                <Chip label="Completed" sx={{ bgcolor: ColorData.primary.main, color: '#fff' }} size="small" />
                                                            </TableCell>
                                                        </StyledTableRow>
                                                    ))
                                                )
                                            ) : (
                                                pendingPayouts.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                                            <Typography variant="h6" color="text.primary">No pending payouts found.</Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    pendingPayouts.map((p) => (
                                                        <StyledTableRow key={`pending-${p.pkPayout}`}>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>#{p.pkPayout}</TableCell>
                                                            <TableCell>{p.caseTitle}</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>#{p.caseId}</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold', color: ColorData.text.primary }}>₹{p.amount.toLocaleString('en-IN')}</TableCell>
                                                            <TableCell>{formatDate(p.date)}</TableCell>
                                                            <TableCell>
                                                                <Chip label="Pending" color="warning" size="small" />
                                                            </TableCell>
                                                        </StyledTableRow>
                                                    ))
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Slide>
                    )}
                </Box>
            </Fade>
        </StyledContainer>
    );
};

export default EMitraPayouts;
